import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, FileText, Info, Lock, Plus, RefreshCw, Save, Trash2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DecimalInput } from "@/components/common/DecimalInput";
import { ConflictBanner } from "@/components/common/ConflictBanner";
import { LoadingList } from "@/components/common/LoadingList";
import { EmptyState } from "@/components/common/EmptyState";
import { HandterEksportDialog, MerkFakturertDialog } from "@/components/invoicing/InvoiceDialogs";
import { api, blockingIssues, downloadFile, errorMessage, isConflict } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney, fmtNumber, parseDecimalInput, toInputDecimal } from "@/lib/format";
import { EXPORT_STATUS_LABELS, HANDLING_LABELS, LINE_KIND_LABELS, UNITS, unitShort } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const GENERATED = ["timar", "ressurs", "vare"];
const MANUAL = ["tillegg", "manuell"];

const localAmount = (line) => {
  const q = Number(line.quantity);
  const p = line.unit_price === null || line.unit_price === undefined || line.unit_price === "" ? null : parseDecimalInput(line.unit_price);
  if (p === null || Number.isNaN(p) || Number.isNaN(q)) return null;
  return Math.round((q * p + Number.EPSILON) * 100) / 100;
};

const Totals = ({ subtotal, vat, total, label, testId, linesSubtotal = null, discountPct = null, discountAmount = null }) => (
  <div className="grid gap-1 rounded-lg border border-border bg-muted p-4 text-sm" data-testid={testId}>
    {label ? <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p> : null}
    {discountAmount ? (
      <>
        <div className="flex justify-between text-muted-foreground"><span>Sum linjer eks. mva</span><span className="num" data-testid={`${testId}-lines-subtotal`}>{fmtMoney(linesSubtotal)}</span></div>
        <div className="flex justify-between text-muted-foreground"><span>Kunderabatt {fmtNumber(discountPct, 1)} %</span><span className="num" data-testid={`${testId}-discount`}>−{fmtMoney(discountAmount)}</span></div>
      </>
    ) : null}
    <div className="flex justify-between"><span>Sum eks. mva</span><span className="num font-semibold" data-testid={`${testId}-subtotal`}>{fmtMoney(subtotal)}</span></div>
    <div className="flex justify-between text-muted-foreground"><span>Meirverdiavgift</span><span className="num">{fmtMoney(vat)}</span></div>
    <div className="flex justify-between border-t border-border pt-1 text-base"><span className="font-semibold">Sum inkl. mva</span><span className="num font-semibold">{fmtMoney(total)}</span></div>
  </div>
);

const IssueList = ({ issues, variant = "destructive", title, testId }) =>
  issues?.length ? (
    <Alert variant={variant} className={cn("bg-card", variant === "default" && "border-warning")} data-testid={testId}>
      {variant === "destructive" ? <AlertTriangle className="h-4 w-4" /> : <Info className="h-4 w-4" />}
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <ul className="list-disc space-y-1 pl-4">{issues.map((i, idx) => <li key={`${i.code}-${idx}`}>{i.message}</li>)}</ul>
      </AlertDescription>
    </Alert>
  ) : null;

/* -------- Linje i kladd (redigerbar) -------- */
const DraftLine = ({ line, mode, onChange, onRemove, otherDescription }) => {
  const generated = GENERATED.includes(line.kind);
  const manual = MANUAL.includes(line.kind);
  const fixed = line.kind === "fast";
  const amount = localAmount(line);
  const priceMissing = amount === null;
  const disabledInclude = line.informational || fixed || (mode === "fixed" && generated);
  return (
    <div className={cn("rounded-lg border border-border bg-card p-3 md:p-4", !line.included && "opacity-80")} data-testid="basis-line" data-line-kind={line.kind}>
      <div className="flex items-start gap-3">
        <div className="pt-1">
          <Checkbox checked={Boolean(line.included)} disabled={disabledInclude} onCheckedChange={(c) => onChange({ included: Boolean(c) })} aria-label="Ta med i grunnlaget" data-testid="basis-line-included" />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-secondary px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-secondary-foreground">{LINE_KIND_LABELS[line.kind]}</span>
            {line.date ? <span className="num text-xs text-muted-foreground">{fmtDate(line.date)}</span> : null}
            {line.informational ? <span className="text-xs text-muted-foreground">Informasjon – inngår i fastprisen</span> : null}
            {line.conflict ? <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-1.5 py-0.5 text-xs font-medium text-warning"><AlertTriangle className="h-3 w-3" /> Konflikt: førar inkludert</span> : null}
            {priceMissing && !line.informational ? <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-1.5 py-0.5 text-xs font-medium text-destructive"><AlertTriangle className="h-3 w-3" /> Manglar pris</span> : null}
            {line.price_overridden ? <span className="text-xs text-muted-foreground">Pris overstyrt</span> : line.price_source === "kundeavtale" ? <span className="rounded-md bg-tint-info px-1.5 py-0.5 text-[11px] font-medium text-info" data-testid="basis-line-price-source">Kundeavtale</span> : null}
          </div>
          {manual ? (
            <Input value={line.description} onChange={(e) => onChange({ description: e.target.value })} className="h-11 bg-card" placeholder="Skildring" data-testid="basis-line-description" />
          ) : (
            <p className="text-sm font-medium" data-testid="basis-line-description">{line.description}</p>
          )}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Mengde</Label>
              {manual ? <DecimalInput value={toInputDecimal(line.quantity)} onChange={(v) => onChange({ quantity_input: v, quantity: parseDecimalInput(v) ?? 0 })} data-testid="basis-line-quantity" /> : <p className="num h-11 leading-[2.75rem]">{fmtNumber(line.quantity, 3)}</p>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Eining</Label>
              {manual ? (
                <Select value={line.unit} onValueChange={(v) => onChange({ unit: v })}>
                  <SelectTrigger  data-testid="basis-line-unit"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">{UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : <p className="h-11 leading-[2.75rem]">{unitShort(line.unit)}</p>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Einingspris eks. mva</Label>
              {fixed ? <p className="num h-11 leading-[2.75rem]">{fmtMoney(line.unit_price)}</p> : (
                <DecimalInput value={line.unit_price_input ?? toInputDecimal(line.unit_price)} onChange={(v) => onChange({ unit_price_input: v, unit_price: v === "" ? null : v })} placeholder="0,00" data-testid="basis-line-price" aria-invalid={line.unit_price !== null && line.unit_price !== undefined && Number.isNaN(parseDecimalInput(line.unit_price))} />
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Beløp eks. mva</Label>
              <p className="num h-11 text-right font-semibold leading-[2.75rem]" data-testid="basis-line-amount">{amount === null ? "–" : fmtMoney(amount)}</p>
            </div>
          </div>
          {line.conflict ? (
            <div className="space-y-1 rounded-lg border border-border bg-muted p-3">
              <p className="text-xs text-muted-foreground">{line.conflict_note}{otherDescription ? ` (${otherDescription})` : ""}</p>
              {line.included ? (
                <>
                  <Label htmlFor={`override-${line.id}`} className="text-xs">Grunngjeving for å fakturere begge (påkravd)</Label>
                  <Input id={`override-${line.id}`} value={line.override_note || ""} onChange={(e) => onChange({ override_note: e.target.value })} className="h-11 bg-card" placeholder="t.d. Avtalt med kunde at sjåførtimar kjem i tillegg" data-testid="basis-line-override-note" />
                </>
              ) : null}
            </div>
          ) : null}
          {line.kind === "tillegg" && mode === "fixed" ? (
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={Boolean(line.approved_addition)} onCheckedChange={(c) => onChange({ approved_addition: Boolean(c) })} data-testid="basis-line-approved-addition" /> Tillegget er eksplisitt godkjent (kunde/avtale)
            </label>
          ) : null}
        </div>
        {manual ? (
          <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-destructive hover:text-destructive" onClick={onRemove} aria-label="Fjern linje" data-testid="basis-line-remove"><Trash2 className="h-4 w-4" /></Button>
        ) : null}
      </div>
    </div>
  );
};

/* -------- Linje (berre lesing) -------- */
const ReadLine = ({ line }) => (
  <div className={cn("grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 border-b border-border px-3 py-3 last:border-b-0 md:grid-cols-[1fr_90px_60px_110px_120px] md:items-center md:px-4", !line.included && "text-muted-foreground")} data-testid="basis-read-line">
    <div className="min-w-0">
      <p className="truncate text-sm font-medium">{line.description}</p>
      <p className="text-xs text-muted-foreground">{LINE_KIND_LABELS[line.kind]}{line.date ? ` · ${fmtDate(line.date)}` : ""}{line.price_source === "kundeavtale" && !line.price_overridden ? " · kundeavtale" : ""}{line.price_overridden ? " · pris overstyrt" : ""}{!line.included ? " · ikkje med" : ""}{line.override_note ? ` · ${line.override_note}` : ""}</p>
    </div>
    <p className="num text-right text-sm font-semibold md:order-5">{line.included ? fmtMoney(line.amount) : "–"}</p>
    <p className="num text-xs text-muted-foreground md:order-2 md:text-right md:text-sm">{fmtNumber(line.quantity, 3)}</p>
    <p className="text-xs text-muted-foreground md:order-3 md:text-sm">{unitShort(line.unit)}</p>
    <p className="num text-xs text-muted-foreground md:order-4 md:text-right md:text-sm">{fmtMoney(line.unit_price)}</p>
  </div>
);

const ExportList = ({ exports, basis, onHandle }) => {
  if (!exports?.length) return null;
  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card" data-testid="export-list">
      {exports.map((ex) => (
        <div key={ex.id} className="flex flex-wrap items-center gap-3 p-3 md:p-4" data-testid="export-item">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-secondary text-primary">{ex.format === "pdf" ? <FileText className="h-4 w-4" /> : <FileSpreadsheet className="h-4 w-4" />}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{ex.filename}</p>
            <p className="text-xs text-muted-foreground"><span className="num">{fmtDateTime(ex.exported_at)}</span> · {ex.exported_by_name} · {ex.row_count} rader · <span className="num">{fmtMoney(ex.subtotal_excl_vat)}</span> · sha256 <span className="num">{ex.checksum.slice(0, 10)}</span></p>
            <p className="text-xs">
              <span className={cn("font-medium", ex.status === "aktiv" ? "text-primary" : "text-muted-foreground")}>{EXPORT_STATUS_LABELS[ex.status] || ex.status}</span>
              {ex.status === "handtert" ? <span className="text-muted-foreground"> · {HANDLING_LABELS[ex.handling] || ex.handling}: {ex.handling_note} ({ex.handled_by_name})</span> : null}
            </p>
          </div>
          <Button variant="outline" size="sm" className="h-10 bg-card" onClick={() => downloadFile(`/exports/${ex.id}/download`, ex.filename).catch((err) => toast.error("Nedlasting feila", { description: errorMessage(err) }))} data-testid="export-download-button">
            <Download className="mr-2 h-4 w-4" /> Last ned
          </Button>
          {ex.status === "aktiv" && basis.status === "erstatta" && onHandle ? (
            <Button size="sm" className="h-10" onClick={() => onHandle(ex)} data-testid="export-handle-button">Handter</Button>
          ) : null}
        </div>
      ))}
    </div>
  );
};

/* -------- Hovudpanel -------- */
export const FakturagrunnlagPanel = ({ order }) => {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [newRevOpen, setNewRevOpen] = useState(false);
  const [invoicedOpen, setInvoicedOpen] = useState(false);
  const [handleExport, setHandleExport] = useState(null);
  const [approveIssues, setApproveIssues] = useState(null);

  const bases = useQuery({ queryKey: ["bases", order.id], queryFn: async () => (await api.get(`/orders/${order.id}/invoice-bases`)).data });

  useEffect(() => {
    if (!bases.data) return;
    if (selectedId && bases.data.some((b) => b.id === selectedId)) return;
    const draftBasis = bases.data.find((b) => b.status === "kladd");
    const approved = bases.data.find((b) => b.status === "godkjent");
    setSelectedId((draftBasis || approved || bases.data[0])?.id || null);
  }, [bases.data, selectedId]);

  const basisQuery = useQuery({ queryKey: ["basis", selectedId], queryFn: async () => (await api.get(`/invoice-bases/${selectedId}`)).data, enabled: Boolean(selectedId) });
  const basis = basisQuery.data;

  useEffect(() => {
    if (basis && basis.status === "kladd") {
      setDraft({ pricing_mode: basis.pricing_mode, fixed_price: toInputDecimal(basis.fixed_price), discount_pct: toInputDecimal(basis.discount_pct), note: basis.note || "", lines: basis.lines.map((l) => ({ ...l })) });
      setDirty(false);
      setConflict(null);
    } else {
      setDraft(null);
      setDirty(false);
    }
  }, [basis]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["bases", order.id] });
    queryClient.invalidateQueries({ queryKey: ["basis"] });
    queryClient.invalidateQueries({ queryKey: ["order", order.id] });
    queryClient.invalidateQueries({ queryKey: ["orders"] });
    queryClient.invalidateQueries({ queryKey: ["counts"] });
    queryClient.invalidateQueries({ queryKey: ["invoicing"] });
    queryClient.invalidateQueries({ queryKey: ["history", order.id] });
  };

  const onError = (err, title) => {
    if (isConflict(err)) {
      setConflict(errorMessage(err));
      return;
    }
    const issues = blockingIssues(err);
    if (issues) {
      setApproveIssues(issues);
      return;
    }
    toast.error(title, { description: errorMessage(err) });
  };

  const createDraft = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/invoice-bases`)).data,
    onSuccess: (data) => {
      invalidateAll();
      setSelectedId(data.id);
      toast.success(`Kladd (revisjon ${data.revision}) oppretta`, { description: "Linjene er henta frå registreringane med gjeldande prisar." });
    },
    onError: (err) => onError(err, "Kunne ikkje lage kladd"),
  });

  const save = useMutation({
    mutationFn: async () => {
      const lines = draft.lines.map((l) => ({
        id: GENERATED.includes(l.kind) || l.kind === "fast" || !l.id?.startsWith("ny-") ? l.id : undefined,
        kind: l.kind,
        description: l.description,
        quantity: l.quantity_input ?? l.quantity,
        unit: l.unit,
        unit_price: l.unit_price === "" ? null : l.unit_price,
        vat_rate: l.vat_rate,
        included: l.included,
        override_note: l.override_note || null,
        approved_addition: l.approved_addition,
      }));
      return (await api.put(`/invoice-bases/${basis.id}`, { version: basis.version, pricing_mode: draft.pricing_mode, fixed_price: draft.pricing_mode === "fixed" ? draft.fixed_price || null : null, discount_pct: draft.pricing_mode === "fixed" ? null : draft.discount_pct || null, note: draft.note || null, lines })).data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["basis", data.id], data);
      invalidateAll();
      toast.success("Fakturagrunnlaget er lagra", { description: `Sum eks. mva ${fmtMoney(data.subtotal_excl_vat)}` });
    },
    onError: (err) => onError(err, "Kunne ikkje lagre"),
  });

  const regenerate = useMutation({
    mutationFn: async () => (await api.post(`/invoice-bases/${basis.id}/regenerate`, { version: basis.version })).data,
    onSuccess: (data) => {
      queryClient.setQueryData(["basis", data.id], data);
      invalidateAll();
      toast.success("Kladden er oppdatert frå registreringane");
    },
    onError: (err) => onError(err, "Kunne ikkje oppdatere"),
  });

  const approve = useMutation({
    mutationFn: async () => (await api.post(`/invoice-bases/${basis.id}/approve`, { version: basis.version })).data,
    onSuccess: (data) => {
      invalidateAll();
      setApproveIssues(null);
      toast.success(`Revisjon ${data.revision} er godkjend og låst`, { description: `${fmtMoney(data.subtotal_excl_vat)} eks. mva · klar til fakturering` });
    },
    onError: (err) => onError(err, "Kunne ikkje godkjenne"),
  });

  const discard = useMutation({
    mutationFn: async () => (await api.delete(`/invoice-bases/${basis.id}`)).data,
    onSuccess: () => {
      setSelectedId(null);
      invalidateAll();
      toast.success("Kladden er forkasta");
    },
    onError: (err) => onError(err, "Kunne ikkje forkaste"),
  });

  const exportMutation = useMutation({
    mutationFn: async (format) => (await api.post(`/invoice-bases/${basis.id}/exports`, { format })).data,
    onSuccess: async (data) => {
      invalidateAll();
      toast.success(`${data.format.toUpperCase()} eksportert`, { description: `${data.filename} · ${data.row_count} rader` });
      try {
        await downloadFile(`/exports/${data.id}/download`, data.filename);
      } catch (err) {
        toast.error("Nedlasting feila", { description: errorMessage(err) });
      }
    },
    onError: (err) => onError(err, "Eksport feila"),
  });

  const updateLine = (id, patch) => {
    setDraft((d) => ({ ...d, lines: d.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
    setDirty(true);
  };
  const removeLine = (id) => {
    setDraft((d) => ({ ...d, lines: d.lines.filter((l) => l.id !== id) }));
    setDirty(true);
  };
  const addLine = (kind) => {
    setDraft((d) => ({
      ...d,
      lines: [...d.lines, { id: `ny-${Date.now()}`, kind, description: "", quantity: 1, quantity_input: "1", unit: "stk", unit_price: null, unit_price_input: "", vat_rate: 25, included: true, approved_addition: kind === "tillegg", override_note: null }],
    }));
    setDirty(true);
  };

  const localTotals = useMemo(() => {
    if (!draft) return null;
    const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
    let linesSubtotal = 0;
    let discountBase = 0;
    for (const l of draft.lines) {
      const generated = GENERATED.includes(l.kind);
      if (draft.pricing_mode === "fixed" && generated) continue;
      if (l.kind === "fast") {
        if (draft.pricing_mode === "fixed") linesSubtotal += parseDecimalInput(draft.fixed_price) || 0;
        continue;
      }
      if (l.kind === "tillegg" && draft.pricing_mode === "fixed" && !l.approved_addition) continue;
      if (!l.included) continue;
      const a = localAmount(l);
      if (a !== null) {
        linesSubtotal += a;
        if (generated) discountBase += a;
      }
    }
    if (draft.pricing_mode === "fixed" && !draft.lines.some((l) => l.kind === "fast")) linesSubtotal += parseDecimalInput(draft.fixed_price) || 0;
    linesSubtotal = round2(linesSubtotal);
    const pct = draft.pricing_mode === "fixed" ? 0 : parseDecimalInput(draft.discount_pct) || 0;
    const discountAmount = pct > 0 && discountBase > 0 ? round2((discountBase * pct) / 100) : 0;
    const subtotal = round2(linesSubtotal - discountAmount);
    const vat = round2(subtotal * 0.25);
    return { linesSubtotal, discountPct: pct, discountAmount, subtotal, vat, total: round2(subtotal + vat) };
  }, [draft]);

  if (bases.isLoading) return <LoadingList rows={2} />;
  const list = bases.data || [];
  const hasDraft = list.some((b) => b.status === "kladd");
  const approvedBasis = list.find((b) => b.status === "godkjent");

  return (
    <div className="space-y-4" data-testid="fakturagrunnlag-panel">
      {conflict ? <ConflictBanner message={conflict} onReload={() => { setConflict(null); basisQuery.refetch(); bases.refetch(); }} testId="basis-conflict-banner" /> : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="basis-revision-list">
          {list.length === 0 ? <p className="text-sm text-muted-foreground">Ingen fakturagrunnlag enno.</p> : null}
          {list.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => setSelectedId(b.id)}
              className={cn("inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", selectedId === b.id ? "border-primary bg-secondary" : "border-border bg-card hover:bg-muted")}
              data-testid={`basis-revision-${b.revision}`}
            >
              Rev. {b.revision} <StatusBadge kind="basis" status={b.status} /> <span className="num text-muted-foreground">{fmtMoney(b.subtotal_excl_vat)}</span>
            </button>
          ))}
        </div>
        {!hasDraft && order.status !== "avlyst" ? (
          <Button className="h-11 md:h-10" onClick={() => (approvedBasis ? setNewRevOpen(true) : createDraft.mutate())} disabled={createDraft.isPending} data-testid="basis-create-draft-button">
            <Plus className="mr-2 h-4 w-4" /> {approvedBasis ? "Ny revisjon (korrigering)" : "Lag fakturagrunnlag"}
          </Button>
        ) : null}
      </div>

      {list.length === 0 && order.status !== "avlyst" ? (
        <EmptyState icon={FileText} title="Ikkje noko fakturagrunnlag" description={order.status === "utfort" ? "Ordra er meld utført. Lag eit fakturagrunnlag frå registreringane." : "Du kan lage ein kladd no, men normalt ventar ein til ordra er meld utført."} data-testid="basis-empty" />
      ) : null}

      {basisQuery.isLoading && selectedId ? <LoadingList rows={2} /> : null}

      {/* ---------- KLADD ---------- */}
      {basis && basis.status === "kladd" && draft ? (
        <div className="space-y-4" data-testid="basis-draft-editor">
          <IssueList issues={basis.warnings} variant="default" title="Merk" testId="basis-warnings" />
          <IssueList issues={approveIssues || basis.blocking_issues} title="Må løysast før godkjenning" testId="basis-blocking-issues" />

          <div className="grid gap-3 rounded-lg border border-border bg-card p-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Prising</Label>
              <Select value={draft.pricing_mode} onValueChange={(v) => { setDraft((d) => ({ ...d, pricing_mode: v })); setDirty(true); }}>
                <SelectTrigger  data-testid="basis-pricing-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover">
                  <SelectItem value="per_unit">Per eining</SelectItem>
                  <SelectItem value="fixed">Fastpris</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {draft.pricing_mode === "fixed" ? (
              <div className="space-y-2">
                <Label htmlFor="basis-fixed">Fastpris eks. mva (kr)</Label>
                <DecimalInput id="basis-fixed" value={draft.fixed_price} onChange={(v) => { setDraft((d) => ({ ...d, fixed_price: v })); setDirty(true); }} placeholder="t.d. 5000" data-testid="basis-fixed-price" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="basis-discount">Kunderabatt (%)</Label>
                <DecimalInput id="basis-discount" value={draft.discount_pct ?? ""} onChange={(v) => { setDraft((d) => ({ ...d, discount_pct: v })); setDirty(true); }} placeholder="0" data-testid="basis-discount-pct" />
                <p className="text-xs text-muted-foreground">Henta frå kundekortet. Gjeld timar, ressursar og varer – ikkje tillegg.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="basis-note">Merknad på grunnlaget</Label>
              <Input id="basis-note" value={draft.note} onChange={(e) => { setDraft((d) => ({ ...d, note: e.target.value })); setDirty(true); }} className="h-11 bg-card" data-testid="basis-note" />
            </div>
          </div>

          <div className="space-y-2" data-testid="basis-lines">
            {draft.lines.map((l) => (
              <DraftLine key={l.id} line={l} mode={draft.pricing_mode} onChange={(p) => updateLine(l.id, p)} onRemove={() => removeLine(l.id)} otherDescription={draft.lines.find((x) => x.id === l.conflict_with_line_id)?.description} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="h-11 bg-card md:h-10" onClick={() => addLine("tillegg")} data-testid="basis-add-tillegg"><Plus className="mr-2 h-4 w-4" /> Legg til tillegg</Button>
            <Button variant="outline" className="h-11 bg-card md:h-10" onClick={() => addLine("manuell")} data-testid="basis-add-manuell"><Plus className="mr-2 h-4 w-4" /> Legg til manuell linje</Button>
          </div>

          <Totals
            subtotal={dirty ? localTotals.subtotal : basis.subtotal_excl_vat}
            vat={dirty ? localTotals.vat : basis.vat_amount}
            total={dirty ? localTotals.total : basis.total_incl_vat}
            linesSubtotal={dirty ? localTotals.linesSubtotal : basis.lines_subtotal_excl_vat}
            discountPct={dirty ? localTotals.discountPct : basis.discount_pct}
            discountAmount={dirty ? localTotals.discountAmount : basis.discount_amount_excl_vat}
            label={dirty ? "Førebels sum (ikkje lagra)" : "Sum"}
            testId="basis-totals"
          />

          <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2">
              <Button className="h-11 md:h-10" onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="basis-save-button"><Save className="mr-2 h-4 w-4" /> {save.isPending ? "Lagrar …" : "Lagre"}</Button>
              <Button variant="outline" className="h-11 bg-card md:h-10" onClick={() => regenerate.mutate()} disabled={regenerate.isPending} data-testid="basis-regenerate-button"><RefreshCw className="mr-2 h-4 w-4" /> Oppdater frå registreringane</Button>
              <Button variant="outline" className="h-11 bg-card text-destructive hover:text-destructive" onClick={() => setDiscardOpen(true)} data-testid="basis-discard-button"><Trash2 className="mr-2 h-4 w-4" /> Forkast kladd</Button>
            </div>
            <div className="flex flex-col items-start gap-1 md:items-end">
              <Button className="h-11 md:h-10" onClick={() => setApproveOpen(true)} disabled={dirty || basis.blocking_issues.length > 0 || approve.isPending} data-testid="basis-approve-button"><Lock className="mr-2 h-4 w-4" /> Godkjenn og lås revisjon {basis.revision}</Button>
              {dirty ? <p className="text-xs text-muted-foreground">Lagre endringane før godkjenning.</p> : basis.blocking_issues.length ? <p className="text-xs text-destructive">Løys punkta over før godkjenning.</p> : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* ---------- GODKJENT / ERSTATTA ---------- */}
      {basis && basis.status !== "kladd" ? (
        <div className="space-y-4" data-testid="basis-readonly">
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-4 text-sm">
            <Lock className="h-4 w-4 text-muted-foreground" />
            <span>
              Revisjon {basis.revision} vart godkjend <span className="num">{fmtDateTime(basis.approved_at)}</span> av {basis.approved_by_name} og er låst.
              {basis.status === "erstatta" ? " Revisjonen er erstatta av ein nyare revisjon og er berre historikk." : ""}
            </span>
            <span className="ml-auto"><StatusBadge kind="basis" status={basis.status} /></span>
          </div>

          {basis.invoice_confirmation ? (
            <Alert className="border-success bg-card" data-testid="basis-invoiced-info">
              <CheckCircle2 className="h-4 w-4 text-success" />
              <AlertTitle>Fakturert</AlertTitle>
              <AlertDescription>Faktura {basis.invoice_confirmation.invoice_reference} datert <span className="num">{fmtDate(basis.invoice_confirmation.invoice_date)}</span>, registrert av {basis.invoice_confirmation.confirmed_by_name} <span className="num">{fmtDateTime(basis.invoice_confirmation.confirmed_at)}</span>.{basis.invoice_confirmation.note ? ` ${basis.invoice_confirmation.note}` : ""}</AlertDescription>
            </Alert>
          ) : null}

          {basis.status === "godkjent" && basis.unhandled_exports?.length ? (
            <Alert className="border-warning bg-card" data-testid="basis-unhandled-exports">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle>Tidlegare eksport må handterast</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>Ein erstatta revisjon er allereie eksportert. Dokumenter kva som er gjort med desse før du eksporterer den nye revisjonen.</p>
                <div className="flex flex-wrap gap-2">
                  {basis.unhandled_exports.map((ex) => (
                    <Button key={ex.id} size="sm" variant="outline" className="h-10 bg-card" onClick={() => setHandleExport(ex)} data-testid="basis-handle-export-button">Handter {ex.format.toUpperCase()} rev. {ex.revision}</Button>
                  ))}
                </div>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="hidden grid-cols-[1fr_90px_60px_110px_120px] gap-x-3 bg-muted px-4 py-2 text-xs font-medium text-muted-foreground md:grid">
              <span>Skildring</span><span className="text-right">Mengde</span><span>Eining</span><span className="text-right">Pris</span><span className="text-right">Beløp eks. mva</span>
            </div>
            {basis.lines.map((l) => <ReadLine key={l.id} line={l} />)}
          </div>
          <Totals subtotal={basis.subtotal_excl_vat} vat={basis.vat_amount} total={basis.total_incl_vat} linesSubtotal={basis.lines_subtotal_excl_vat} discountPct={basis.discount_pct} discountAmount={basis.discount_amount_excl_vat} testId="basis-totals" />
          {basis.note ? <p className="text-sm text-muted-foreground">Merknad: {basis.note}</p> : null}

          {basis.status === "godkjent" ? (
            <div className="flex flex-col gap-2 md:flex-row md:flex-wrap">
              <Button variant="outline" className="h-11 bg-card md:h-10" onClick={() => exportMutation.mutate("pdf")} disabled={exportMutation.isPending || basis.unhandled_exports?.length > 0} data-testid="fakturering-export-pdf-button"><FileText className="mr-2 h-4 w-4" /> Eksporter PDF</Button>
              <Button variant="outline" className="h-11 bg-card md:h-10" onClick={() => exportMutation.mutate("csv")} disabled={exportMutation.isPending || basis.unhandled_exports?.length > 0} data-testid="fakturering-export-csv-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Eksporter CSV</Button>
              {!basis.invoice_confirmation ? <Button className="h-11 md:h-10" onClick={() => setInvoicedOpen(true)} data-testid="fakturering-merk-som-fakturert-button"><CheckCircle2 className="mr-2 h-4 w-4" /> Merk som fakturert</Button> : null}
            </div>
          ) : null}

          <ExportList exports={basis.exports} basis={basis} onHandle={(ex) => setHandleExport(ex)} />
        </div>
      ) : null}

      {/* ---------- Dialogar ---------- */}
      <AlertDialog open={approveOpen} onOpenChange={setApproveOpen}>
        <AlertDialogContent className="bg-card" data-testid="basis-approve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Godkjenne og låse revisjon {basis?.revision}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sum eks. mva {fmtMoney(basis?.subtotal_excl_vat)}. Etter godkjenning kan ikkje linjene endrast; seinare endringar i katalog eller kunde påverkar ikkje denne revisjonen. Korrigeringar krev ny revisjon.
              {approvedBasis ? ` Revisjon ${approvedBasis.revision} blir merkt som erstatta.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 bg-card md:h-10" data-testid="basis-approve-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="h-11 md:h-10" onClick={() => approve.mutate()} data-testid="basis-approve-confirm">Godkjenn</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogContent className="bg-card" data-testid="basis-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Forkaste kladden?</AlertDialogTitle>
            <AlertDialogDescription>Kladden blir sletta. Registreringane på ordra blir ikkje rørte.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 bg-card md:h-10">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="h-11 bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => discard.mutate()} data-testid="basis-discard-confirm">Forkast</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={newRevOpen} onOpenChange={setNewRevOpen}>
        <AlertDialogContent className="bg-card" data-testid="basis-new-revision-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Lage ny revisjon?</AlertDialogTitle>
            <AlertDialogDescription>Ein ny kladd blir laga frå gjeldande registreringar og prisar. Revisjon {approvedBasis?.revision} blir ståande som gjeldande til den nye blir godkjend, og blir deretter merkt som erstatta. Historikk og eksportfiler blir tekne vare på.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 bg-card md:h-10">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="h-11 md:h-10" onClick={() => createDraft.mutate()} data-testid="basis-new-revision-confirm">Lag ny revisjon</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MerkFakturertDialog open={invoicedOpen} onOpenChange={setInvoicedOpen} basis={basis} />
      <HandterEksportDialog open={Boolean(handleExport)} onOpenChange={(o) => !o && setHandleExport(null)} exportItem={handleExport} basis={basis} />
    </div>
  );
};
